import {
  albumCollectionByIdQuery,
  albumCollectionByIdRefreshed,
} from '@/entities/album-collections'
import { albumNameUpdate } from '@/entities/albums'
import {
  TrackUploadStatus,
  type TracksQueryModelBase,
  createTracksQueryModel,
} from '@/entities/tracks'
import {
  type AlbumCollectionInfoModel,
  type AlbumCollectionItemModel,
} from '@/shared/api'
import { createModalModel } from '@/shared/lib/model-factories'
import { combine, createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

// Gate
export const AlbumCollectionFormAlbumsGate = createGate<{
  AlbumCollectionInfo: AlbumCollectionInfoModel
}>()

// Tracks
export const tracksAlbumCollectionsAlbumsQueryModel = createTracksQueryModel({
  initialFilters: {
    UploadStatuses: [TrackUploadStatus.Uploaded],
  },
  usePagination: false,
  accumulate: false,
}) as TracksQueryModelBase
const {
  availableSet: tracksAlbumCollectionAlbumsAvailableSet,
  unavailableSet: tracksAlbumCollectionAlbumsUnavailableSet,
} = tracksAlbumCollectionsAlbumsQueryModel
export const {
  //   Query
  query: tracksAlbumCollectionAlbumsQuery,
  //   Init
  filtersInit: tracksAlbumCollectionAlbumsFiltersInit,
  //   Refresh
  refreshed: tracksAlbumCollectionAlbumsRefreshed,
} = tracksAlbumCollectionsAlbumsQueryModel

sample({
  clock: AlbumCollectionFormAlbumsGate.open,
  target: tracksAlbumCollectionAlbumsAvailableSet,
})
sample({
  clock: AlbumCollectionFormAlbumsGate.close,
  target: tracksAlbumCollectionAlbumsUnavailableSet,
})

sample({
  source: AlbumCollectionFormAlbumsGate.state,
  clock: tracksAlbumCollectionAlbumsAvailableSet,
  fn: (payload) => {
    const albumCollection = payload.AlbumCollectionInfo
    return {
      AlbumIds:
        (albumCollection.albums && albumCollection.albums.map((a) => a.id)) ||
        [],
    }
  },
  target: tracksAlbumCollectionAlbumsFiltersInit,
})

// Order
export const albumsReordered = createEvent<AlbumCollectionItemModel[]>()
export const albumsOrderReset = createEvent()

const $albumsOrder = createStore<string[]>([])
  .on(albumsReordered, (_, newOrder) => newOrder.map((a) => a.id))
  .on(albumsOrderReset, () => [])

export const $albumsOrdered = combine(
  albumCollectionByIdQuery.$data,
  $albumsOrder,
  (albumCollection, order): AlbumCollectionItemModel[] => {
    if (!albumCollection || !albumCollection.albums) return []
    const albums = albumCollection.albums
    if (!order.length) return albums

    const map = new Map(albums.map((a) => [a.id, a]))

    const reordered = order
      .map((id) => map.get(id))
      .filter((a): a is AlbumCollectionItemModel => !!a)

    const rest = albums.filter((a) => !order.includes(a.id))

    return [...reordered, ...rest]
  },
)

// Order Modal
export const {
  modalOpened: albumOrderModalOpened,
  modalClosed: albumOrderModalClosed,
  $isModalOpened: $isAlbumOrderModalOpen,
} = createModalModel()

// Album Name Update

sample({
  clock: albumNameUpdate.finished.success,
  target: albumCollectionByIdRefreshed,
})
